import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Shield, Award, Users, Heart, Briefcase, GraduationCap, Clock, MapPin } from 'lucide-react';
import { SEO } from '../components/SEO';
import { 
  fadeInUp, 
  fadeInLeft, 
  fadeInRight, 
  scaleIn,
  staggerContainer,
  staggerItem,
  timelineItem,
  viewportSettings
} from '../hooks/useScrollAnimation';

export const About = () => {
  const achievements = [
    { icon: Clock, title: '15 Years', description: 'Serving with Marshall County Sheriff\'s Office' },
    { icon: GraduationCap, title: 'DOCJT Certified', description: '18 weeks of professional police training' },
    { icon: Shield, title: 'Court Security', description: '3 years protecting the court system' },
    { icon: MapPin, title: 'Lifelong Resident', description: 'Born and raised in Marshall County' }
  ];

  const timelineItems = [
    { icon: Briefcase, date: 'General Tire, Mayfield', title: 'Manufacturing Career', description: 'Worked for 5 years until the facility closed, learning the value of hard work and dedication.' },
    { icon: Briefcase, date: 'Hopkins Construction', title: 'Construction Work', description: 'Transitioned careers before finding his calling in law enforcement.' },
    { icon: Shield, date: 'March 2010', title: 'Marshall County Sheriff\'s Office', description: 'Began public service career as a court security officer.' },
    { icon: GraduationCap, date: 'March 2013', title: 'DOCJT Police Training', description: 'Completed 18 weeks of intensive police coursework at the Department of Criminal Justice Training.' },
    { icon: Award, date: 'Present Day', title: '15 Years of Service', description: 'Continuing to serve Marshall County with dedication and commitment.', highlight: true }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-16 sm:pt-20">
      <SEO 
        title="About Barry Howell | Marshall County Jailer Candidate"
        description="Learn about Barry Howell's 15-year career in law enforcement and his vision for the Marshall County Jail. A lifelong resident of Benton, KY dedicated to public safety."
        keywords="Barry Howell bio, Marshall County Jailer candidate, Benton KY, law enforcement experience, Marshall County Sheriff's Office"
        canonical="https://mcjailer.com/about"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
              "@type": "Person",
              "name": "Barry Howell",
              "jobTitle": "Candidate for Marshall County Jailer",
              "description": "15-year veteran of the Marshall County Sheriff's Office running for County Jailer.",
              "image": "https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/ya6nzdiw_DSC09954.jpg",
              "alumniOf": "Marshall County High School",
              "knowsAbout": ["Law Enforcement", "Public Safety", "Corrections", "Community Service"],
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Benton",
                "addressRegion": "KY"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/8tidws91_DSC09773.jpg"
            alt="Background"
            className="w-full h-full object-cover object-top brightness-[0.35] contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-transparent to-[#0A0A0A]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="text-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">
              Meet the Candidate
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white uppercase tracking-tighter font-['Barlow_Condensed']">
              Barry Howell
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mt-3 sm:mt-4">
              A lifetime of service to Marshall County
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-10 sm:py-16 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-start">
            <motion.div 
              className="w-full lg:w-1/3"
              initial="hidden"
              whileInView="visible"
              viewport={viewportSettings}
              variants={fadeInLeft}
            >
              <div className="aspect-[3/4] max-w-xs mx-auto lg:max-w-none rounded-sm overflow-hidden border border-[#DAA520]/30 lg:sticky lg:top-28 group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/ya6nzdiw_DSC09954.jpg"
                  alt="Barry Howell and his wife"
                  className="w-full h-full object-cover object-top brightness-110 contrast-105 group-hover:scale-105 transition-transform duration-500"
                  data-testid="about-main-photo"
                />
              </div>
            </motion.div>

            <motion.div 
              className="w-full lg:w-2/3"
              initial="hidden"
              whileInView="visible"
              viewport={viewportSettings}
              variants={fadeInRight}
            >
              <div className="bio-card p-6 sm:p-8 md:p-12 mb-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4 sm:mb-6">
                  My Story
                </h2>
                <div className="space-y-4 sm:space-y-6 text-gray-300 leading-relaxed text-sm sm:text-base" data-testid="about-bio">
                  <p>My name is Barry Howell, and I'm a lifelong resident of Marshall County. I'm a proud 1996 graduate of Marshall County High School, and have raised both of my daughters, Alyssa and Abby, here in the county. Today, I’m blessed to be engaged to Lacey Cavitt. Along with her, I’ve been welcomed into the lives of three great bonus kids, Griffin, Samone, and Anissa.</p>
                  <p>My parents are Malcolm and the late Brenda Howell. My dad worked and retired from General Tire in Mayfield, and my beautiful mom was a full-time homemaker who took care of me and my two sisters. I also worked at General Tire for five years until the company closed its doors. After being laid off, I went to work for Hopkins Construction before beginning my law enforcement career.</p>
                  <p>I started my public service career with the Marshall County Sheriff's Office in March 2010. For the first three years, I served as a court security officer, and in March 2013, I attended the Department of Criminal Justice Training, completing 18 weeks of police training. In total, I've been proudly serving with the Marshall County Sheriff's Office for the past 15 years.</p>
                  <p>Over the course of my career, I've experienced both the most rewarding moments and some of the toughest challenges that come with public service. But through it all, one thing has remained constant — my love for this county.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements Grid */}
      <section className="py-12 sm:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="text-center mb-10 sm:mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">
              Experience & Qualifications
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed']">
              A Record of Service
            </h2>
          </motion.div>

          <motion.div 
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" 
            data-testid="achievements-grid"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={staggerContainer}
          >
            {achievements.map((item, index) => (
              <motion.div 
                key={index}
                variants={staggerItem}
                className="bg-[#111111] border border-[#DAA520]/20 p-6 sm:p-8 text-center hover:border-[#DAA520]/50 transition-colors"
              >
                <item.icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#DAA520] mx-auto mb-3 sm:mb-4" />
                <h3 className="text-xl sm:text-2xl font-bold text-white font-['Barlow_Condensed'] uppercase mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Career Timeline */}
      <section className="py-12 sm:py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="text-center mb-10 sm:mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">
              Career Journey
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed']">
              Professional Timeline
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto" data-testid="career-timeline">
            <div className="relative pl-6 sm:pl-8 border-l-2 border-[#DAA520]/30">
              {timelineItems.map((item, index) => (
                <motion.div 
                  key={index}
                  className="relative mb-8 sm:mb-12 last:mb-0"
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewportSettings}
                  variants={timelineItem(index)}
                >
                  <div className={`absolute -left-[25px] sm:-left-[33px] w-3 h-3 sm:w-4 sm:h-4 rounded-full ${
                    item.highlight ? 'bg-[#DAA520] ring-4 ring-[#DAA520]/30' : 'bg-[#DAA520]'
                  }`} />
                  
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#DAA520]" />
                    <span className="text-[#DAA520] font-semibold text-sm sm:text-base">{item.date}</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white font-['Barlow_Condensed'] uppercase">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 mt-2 text-sm sm:text-base">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Family Section */}
      <section className="py-12 sm:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
            <motion.div 
              className="w-full lg:w-1/2"
              initial="hidden"
              whileInView="visible"
              viewport={viewportSettings}
              variants={scaleIn}
            >
              <div className="relative aspect-video rounded-sm overflow-hidden border border-[#DAA520]/30 group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/kj4bnx5h_DSC00011.jpg"
                  alt="Barry Howell's family"
                  className="w-full h-full object-cover object-top brightness-[1.15] contrast-105 group-hover:scale-105 transition-transform duration-500"
                  data-testid="family-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 via-transparent to-transparent"></div>
              </div>
            </motion.div>
            
            <motion.div 
              className="w-full lg:w-1/2" 
              data-testid="family-section"
              initial="hidden"
              whileInView="visible"
              viewport={viewportSettings}
              variants={fadeInRight}
            >
              <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">
                Family Values
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4 sm:mb-6">
                Rooted in Marshall County
              </h2>
              <div className="space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
                <div className="flex items-start gap-3 sm:gap-4">
                  <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-[#DAA520] flex-shrink-0 mt-1" />
                  <p><strong className="text-white">Proud Father</strong> — Raised both daughters right here in Marshall County, instilling the same values of community and service.</p>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#DAA520] flex-shrink-0 mt-1" />
                  <p><strong className="text-white">Family Threads</strong> — Son of Malcolm and the late Brenda Howell. His father retired from General Tire, and his mother devoted her life to caring for Barry and his two sisters.</p>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#DAA520] flex-shrink-0 mt-1" />
                  <p><strong className="text-white">Deep Roots</strong> — A lifelong resident who graduated from Marshall County High School in 1996 and has never left the community he loves.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
